import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { darkTheme } from '../../lib/tokens';
import { useCanWrite } from '../auth/permissions';
import { createReviewDraft } from './api';
import { kanalName } from './bausteine';
import { REVIEW_TARGETS, type ReviewTarget } from './types';

const colors = darkTheme.colors;

export function ReviewDraftPage(): JSX.Element {
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const [title, setTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [channel, setChannel] = useState<ReviewTarget>('YOUTUBE');
  const [date, setDate] = useState('');
  // Ordner im Repo (CUR-2288): Ohne ihn hat der Entwurf kein Kürzel und die Suche findet ihn nicht.
  const [ordner, setOrdner] = useState('');
  // Ohne Angabe ließe sich der Entwurf später nicht einreichen (CUR-2249) — also gleich hier fragen.
  const [aiLabel, setAiLabel] = useState<boolean | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');

  async function create(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canWrite || !title.trim() || !caption.trim() || aiLabel === null) return;
    setPending(true);
    setError('');
    try {
      // Eine App-Fassung bekommt nie einen Termin — sie wird nicht veröffentlicht (CUR-2247).
      const post = await createReviewDraft({
        title: title.trim(), caption: caption.trim(), channel, aiLabel,
        ...(date && channel !== 'APP' ? { scheduledFor: date } : {}),
        ...(ordner.trim() ? { ordner: ordner.trim() } : {}),
      });
      navigate(`/social/post/${post.id}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Der Entwurf ließ sich nicht speichern.');
    } finally {
      setPending(false);
    }
  }

  return <main className="mx-auto max-w-[760px] p-4 lg:p-6" style={{ color: colors.textPrimary }}>
    <Link to="/social" className="text-sm" style={{ color: colors.accentLight }}>‹ Kalender</Link>
    <h1 className="mt-4 text-2xl font-bold">Beitrag zur Prüfung vorbereiten</h1>
    <p className="mt-2 text-sm" style={{ color: colors.textSecondary }}>Der Entwurf ist nur für dich sichtbar. Christine erhält erst beim Einreichen eine E-Mail.</p>
    {!canWrite ? <p className="mt-6">Für diesen Bereich hast du nur Leserechte.</p> :
      <form onSubmit={(event) => void create(event)} className="mt-6 space-y-5">
        <label className="block text-sm font-semibold">Titel
          <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={160} required className="mt-2 w-full rounded-md border p-3 text-base" style={{ backgroundColor: colors.bgSurface, borderColor: colors.border, color: colors.textPrimary }} />
          {/* Seit CUR-2249 der Titel der Fassung, den Christine prüft und YouTube zeigt — kein Arbeitstitel mehr. */}
          <span className="mt-1 block text-xs font-normal" style={{ color: colors.textSecondary }}>Der Titel, den Christine prüft und die Plattform zeigt. Im Beitrag später änderbar.</span>
        </label>
        <label className="block text-sm font-semibold">Kanal
          <select value={channel} onChange={(event) => setChannel(event.target.value as ReviewTarget)} className="mt-2 w-full rounded-md border p-3" style={{ backgroundColor: colors.bgSurface, borderColor: colors.border, color: colors.textPrimary }}>
            {REVIEW_TARGETS.map((value) => <option key={value} value={value}>{value === 'APP' ? 'App-Fassung (nur Fachprüfung, keine Veröffentlichung)' : kanalName(value)}</option>)}
          </select>
        </label>
        {channel !== 'APP' && (
          <label className="block text-sm font-semibold">Geplanter Tag (optional)
            <input type="date" value={date} onChange={(event) => setDate(event.target.value)} className="mt-2 w-full rounded-md border p-3" style={{ backgroundColor: colors.bgSurface, borderColor: colors.border, color: colors.textPrimary }} />
            <span className="mt-1 block text-xs font-normal" style={{ color: colors.textSecondary }}>Ohne Tag steht der Entwurf im Kalender unter „Ohne Termin“.</span>
          </label>
        )}
        <label className="block text-sm font-semibold">Ordner im Repo (optional)
          <input value={ordner} onChange={(event) => setOrdner(event.target.value)} maxLength={300} placeholder="E31 oder docs/marketing/…/E31-hormonsystem-diabetes"
            className="mt-2 w-full rounded-md border p-3 font-mono text-sm" style={{ backgroundColor: colors.bgSurface, borderColor: colors.border, color: colors.textPrimary }} />
          <span className="mt-1 block text-xs font-normal" style={{ color: colors.textSecondary }}>Ein Kürzel wie E31, S03, LC-01 oder der Pfad des Ordners. Damit steht der Beitrag im Kalender unter seinem Kürzel und die Suche findet ihn.</span>
        </label>
        <fieldset>
          <legend className="text-sm font-semibold">KI-generierte Inhalte</legend>
          <div className="mt-2 flex flex-wrap gap-4 text-sm" style={{ color: colors.textSecondary }}>
            <label className="flex min-h-[44px] items-center gap-2"><input type="radio" name="ki" checked={aiLabel === true} onChange={() => setAiLabel(true)} /> ja</label>
            <label className="flex min-h-[44px] items-center gap-2"><input type="radio" name="ki" checked={aiLabel === false} onChange={() => setAiLabel(false)} /> nein</label>
          </div>
          <span className="block text-xs" style={{ color: colors.textSecondary }}>Gilt für alle Plattform-Fassungen und gehört zu dem, was Christine freigibt.</span>
        </fieldset>
        <label className="block text-sm font-semibold">Vollständige Caption / Beschreibung
          <textarea value={caption} onChange={(event) => setCaption(event.target.value)} rows={12} required className="mt-2 w-full rounded-md border p-3 font-mono text-sm" style={{ backgroundColor: colors.bgSurface, borderColor: colors.border, color: colors.textPrimary }} />
        </label>
        {error && <p role="alert" style={{ color: colors.danger }}>{error}</p>}
        <button type="submit" disabled={pending || !title.trim() || !caption.trim() || aiLabel === null} className="min-h-[44px] rounded-lg px-5 font-semibold text-white disabled:opacity-50" style={{ backgroundColor: colors.accent }}>
          {pending ? 'Wird gespeichert …' : 'Entwurf anlegen'}
        </button>
      </form>}
  </main>;
}
